import { useState } from "react";
import { Box, Typography, Chip, Divider, Paper, Tooltip } from "@mui/material";
import StorageIcon      from "@mui/icons-material/Storage";
import WebhookIcon      from "@mui/icons-material/Webhook";
import RouterIcon       from "@mui/icons-material/Router";
import DnsIcon          from "@mui/icons-material/Dns";
import SyslogConfig      from "./SyslogConfig";
import ServerSyslogConfig from "./ServerSyslogConfig";
import KafkaConfig       from "./KafkaConfig";
import WebhookConfig     from "./WebhookConfig";

// ── Integration registry ───────────────────────────────────────
const INTEGRATIONS = [
  {
    id:             "syslog",
    label:          "Syslog",
    description:    "Forward events to a SIEM (CEF / LEEF / JSON)",
    icon:           RouterIcon,
    status:         "active",
    component:      SyslogConfig,
    componentProps: (props) => ({ storedSyslog: props.storedSyslog }),
    // Topic selection lives inside the SyslogConfig component now (the spec
    // ties topics tightly to the syslog config payload), so we don't need
    // the panel-level "Trigger on" section here.
    hideTriggerOn:  true,
  },
  {
    id:          "server_syslog",
    label:       "Cloud Syslog",
    description: "Forward cloud-processed events to a syslog server",
    icon:        DnsIcon,
    status:      "soon",
    component:   ServerSyslogConfig,
    componentProps: () => ({}),
  },
  {
    id:          "webhook",
    label:       "Webhook",
    description: "Send HTTP POST callbacks to an external endpoint",
    icon:        WebhookIcon,
    status:      "soon",
    component:   WebhookConfig,
    componentProps: () => ({}),
  },
  {
    id:             "kafka",
    label:          "Kafka",
    description:    "Stream events to an Apache Kafka topic",
    icon:           StorageIcon,
    status:         "soon",
    component:      KafkaConfig,
    componentProps: () => ({}),
    hideTriggerOn:  true,
  },
];

const TRIGGER_SEVERITIES = [
  { key: "critical",          label: "Critical",            color: "#931A1D" },
  { key: "high",              label: "High",                color: "#FEA51D" },
  { key: "alert",             label: "Alert",               color: "#D1C167" },
  { key: "movements",         label: "Movements",           color: "#459296" },
  { key: "threat",            label: "Threat Intelligence", color: "#C485BF" },
  { key: "security_cases",    label: "Security Cases",      color: "#E67E22" },
  { key: "tiresia_anomalies", label: "Tiresia Anomalies",   color: "#7C4DFF" },
];

// ── Trigger on section ─────────────────────────────────────────
const TriggerOnSection = ({ value, onChange, soon = false }) => (
  <Box>
    <Divider sx={{ mb: 2.5 }} />
    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
      <Typography
        variant="subtitle2"
        sx={{ color: "#aaa", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em" }}
      >
        Trigger on
      </Typography>
      {soon && (
        <Chip size="small" label="Coming soon" sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700, backgroundColor: "rgba(69,144,149,0.18)", color: "#43B4B9", border: "1px solid rgba(69,144,149,0.3)" }} />
      )}
    </Box>
    <Box display="flex" gap={1} flexWrap="wrap" sx={{ opacity: soon ? 0.35 : 1, pointerEvents: soon ? "none" : "auto" }}>
      {TRIGGER_SEVERITIES.map(({ key, label, color }) => {
        const active = value.includes(key);
        return (
          <Tooltip key={key} title={soon ? "" : active ? `Disable for ${label}` : `Enable for ${label}`}>
            <Chip
              label={label}
              size="small"
              onClick={() =>
                onChange(
                  active ? value.filter((k) => k !== key) : [...value, key]
                )
              }
              sx={{
                cursor: "pointer",
                fontWeight: 700,
                fontSize: "0.72rem",
                backgroundColor: active ? `${color}22` : "transparent",
                color:           active ? color : "#555",
                border:          `1px solid ${active ? color : "#333"}`,
                "&:hover": {
                  backgroundColor: `${color}33`,
                  color: color,
                  borderColor: color,
                },
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  </Box>
);

const STATUS_CHIP = {
  active: null,
  soon:   <Chip size="small" label="Coming soon" sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700, backgroundColor: "rgba(69,144,149,0.18)", color: "#43B4B9", border: "1px solid rgba(69,144,149,0.3)" }} />,
};

// ── Sidebar item ───────────────────────────────────────────────
const SidebarItem = ({ integration, selected, onClick }) => {
  const Icon = integration.icon;
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        px: 2, py: 1.25, cursor: "pointer", borderRadius: 1,
        backgroundColor: selected ? "rgba(69,144,149,0.14)" : "transparent",
        borderLeft: selected ? "3px solid #449095" : "3px solid transparent",
        transition: "all 0.15s",
        "&:hover": { backgroundColor: selected ? "rgba(69,144,149,0.14)" : "rgba(255,255,255,0.04)" },
      }}
    >
      <Icon sx={{ fontSize: 20, color: selected ? "#449095" : "#666", flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box display="flex" alignItems="center" gap={0.75}>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: selected ? 700 : 500, color: selected ? "#e8e8e8" : "#aaa", lineHeight: 1.3 }}>
            {integration.label}
          </Typography>
          {STATUS_CHIP[integration.status]}
        </Box>
        <Typography sx={{ fontSize: "0.68rem", color: "#555", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {integration.description}
        </Typography>
      </Box>
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════
const IntegrationsPanel = ({ storedSyslog }) => {
  const [selectedId, setSelectedId] = useState("syslog");
  const [triggers, setTriggers] = useState({
    syslog:        ["critical", "high"],
    server_syslog: ["critical", "high"],
    webhook:       ["critical", "high"],
    kafka:         ["critical", "high"],
  });

  const selected = INTEGRATIONS.find((i) => i.id === selectedId);
  const ComponentToRender = selected?.component ?? null;
  const componentProps = selected?.componentProps({ storedSyslog }) ?? {};

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        minHeight: 520,
        border: "1px solid rgba(69,144,149,0.15)",
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: "rgba(20,20,20,0.8)",
      }}
    >
      {/* ── Sidebar ─────────────────────────────────────────── */}
      <Box
        sx={{
          width: 220,
          flexShrink: 0,
          borderRight: "1px solid #222",
          backgroundColor: "#111",
          py: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 0.25,
        }}
      >
        <Typography
          sx={{ px: 2, pb: 1, fontSize: "0.62rem", fontWeight: 700, color: "#555",
               textTransform: "uppercase", letterSpacing: "0.1em" }}
        >
          Integrations
        </Typography>
        {INTEGRATIONS.map((intg) => (
          <SidebarItem
            key={intg.id}
            integration={intg}
            selected={selectedId === intg.id}
            onClick={() => setSelectedId(intg.id)}
          />
        ))}
      </Box>

      {/* ── Content area ────────────────────────────────────── */}
      <Box sx={{ flex: 1, p: 3.5, overflowY: "auto", display: "flex", flexDirection: "column", gap: 3 }}>
        {ComponentToRender && <ComponentToRender {...componentProps} />}
        {!selected?.hideTriggerOn && (
          <TriggerOnSection
            value={triggers[selectedId] ?? []}
            onChange={(val) => setTriggers((prev) => ({ ...prev, [selectedId]: val }))}
            soon={!!selected?.triggerOnSoon}
          />
        )}
      </Box>
    </Paper>
  );
};

export default IntegrationsPanel;
